import { useEffect, useRef, type RefObject } from 'react';

// Lands an admin form on one field (`?field=<name>` deep links): scrolls the
// field's control into view and focuses it, once per arrival of the field.
// `targets` maps each field the section can land on to its control, so a
// section only names fields it has; pass a stable object (useMemo). Later
// renders (typing elsewhere, a refetch) leave focus alone; a link that drops
// the field and brings it back lands again.
export function useLandOnField<F extends string>(
  field: F | undefined,
  targets: Readonly<Record<F, RefObject<HTMLElement | null>>>,
): void {
  const landedOn = useRef<F | undefined>(undefined);
  useEffect(() => {
    if (field === undefined) {
      landedOn.current = undefined;
      return;
    }
    if (landedOn.current === field) return;
    const control = targets[field].current;
    if (control === null) return;
    landedOn.current = field;
    control.scrollIntoView({ block: 'center' });
    control.focus({ preventScroll: true });
  }, [field, targets]);
}
